import React from "react";
import moment from "moment";
import { Chip, TextField } from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";

import listPageStyles from "../../styles/listPageStyles";

// Peças compartilhadas das abas do Financeiro (mesmo visual de Contatos/Tags).

export const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });

// ─── Cores de estado (claro/escuro) ──────────────────────────────────────────
const TONES = {
  green: { light: "#2e7d32", dark: "#81c784" },
  red: { light: "#c62828", dark: "#e57373" },
  amber: { light: "#b26a00", dark: "#ffd54f" },
  blue: { light: "#1565c0", dark: "#64b5f6" },
  grey: { light: "#757575", dark: "#bdbdbd" },
};

export const toneColor = (theme, tone) =>
  (TONES[tone] || TONES.grey)[theme.palette.type === "dark" ? "dark" : "light"];

export const financeiroStyles = (theme) => {
  const pill = (tone) => ({
    color: toneColor(theme, tone),
    backgroundColor: alpha(toneColor(theme, tone), theme.palette.type === "dark" ? 0.18 : 0.12),
  });
  return {
    ...listPageStyles(theme),
    root: {
      color: theme.palette.text.primary,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    },
    // abas em pílulas
    tabs: {
      display: "flex",
      gap: 4,
      padding: theme.spacing(0, 0.75, 1.75),
      overflowX: "auto",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
    tab: {
      flex: "0 0 auto",
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 999,
      padding: theme.spacing(0.75, 1.75),
      color: theme.palette.text.secondary,
      border: "1px solid transparent",
      minHeight: 0,
      fontSize: 13,
    },
    tabActive: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
    },
    tabBadge: {
      marginLeft: 6,
      minWidth: 18,
      height: 18,
      lineHeight: "18px",
      padding: "0 6px",
      borderRadius: 999,
      fontSize: 11,
      ...pill("red"),
    },
    // faixa de indicadores
    kpis: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 14,
      marginBottom: theme.spacing(1.75),
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        overflowX: "auto",
        gap: theme.spacing(1),
        padding: 0,
        border: "none",
        backgroundColor: "transparent",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      },
    },
    kpi: {
      padding: theme.spacing(1.75, 2.25),
      borderLeft: `1px solid ${theme.palette.divider}`,
      minWidth: 0,
      "&:first-child": { borderLeft: "none" },
      [theme.breakpoints.down("sm")]: {
        flex: "0 0 auto",
        minWidth: 128,
        padding: theme.spacing(1, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        "&:first-child": { border: `1px solid ${theme.palette.divider}` },
      },
    },
    kpiLabel: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
    kpiDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
    kpiValue: {
      fontSize: 20,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      [theme.breakpoints.down("sm")]: { fontSize: 15 },
    },
    kpiCaption: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    // chips de filtro
    chipGroup: { display: "flex", gap: 6, flexWrap: "wrap" },
    chipGroupScroll: {
      flexWrap: "nowrap",
      overflowX: "auto",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
      "& > *": { flex: "0 0 auto" },
    },
    filterChip: { fontWeight: 600, height: 30 },
    dateField: { width: 150 },
    // selos de status
    pill: {
      height: 22,
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 999,
    },
    pillGreen: pill("green"),
    pillRed: pill("red"),
    pillAmber: pill("amber"),
    pillGrey: pill("grey"),
    // valores
    income: { color: toneColor(theme, "green"), fontWeight: 700, fontVariantNumeric: "tabular-nums" },
    expense: { color: toneColor(theme, "red"), fontWeight: 700, fontVariantNumeric: "tabular-nums" },
    amount: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
    lateRow: {
      boxShadow: `inset 3px 0 0 ${toneColor(theme, "red")}`,
    },
    cancelledText: { textDecoration: "line-through", opacity: 0.6 },
    subtle: { fontSize: 12, color: theme.palette.text.secondary },
    lateText: { fontSize: 12, color: toneColor(theme, "red") },
    categoryDot: {
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      marginRight: 6,
      verticalAlign: "middle",
    },
    payButton: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 8,
      height: 30,
      color: toneColor(theme, "green"),
      backgroundColor: alpha(toneColor(theme, "green"), 0.12),
      "&:hover": { backgroundColor: alpha(toneColor(theme, "green"), 0.22) },
    },
    callout: {
      margin: theme.spacing(2, 2.5, 0),
      padding: theme.spacing(1.25, 1.75),
      borderRadius: 10,
      fontSize: 13,
      color: theme.palette.text.primary,
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    // lista mobile
    mobileRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    mobileInfo: { flex: 1, minWidth: 0 },
    mobileTitle: {
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    mobileValue: { textAlign: "right", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 },
  };
};

export const useFinanceiroStyles = makeStyles(financeiroStyles);

// ─── Status ──────────────────────────────────────────────────────────────────

// Status efetivo de um lançamento: pendente com vencimento passado vira "overdue".
export const effectiveStatus = (tx) => {
  if (tx.status !== "pending") return tx.status;
  return moment(tx.dueDate).isBefore(moment(), "day") ? "overdue" : "pending";
};

export const STATUS_PILL = {
  pending: { label: "Pendente", cls: "pillAmber" },
  overdue: { label: "Vencido", cls: "pillRed" },
  paid: { label: "Pago", cls: "pillGreen" },
  cancelled: { label: "Cancelado", cls: "pillGrey" },
};

export const StatusPill = ({ status, label, classes }) => {
  const cfg = STATUS_PILL[status] || STATUS_PILL.pending;
  return (
    <Chip
      size="small"
      label={label || cfg.label}
      className={`${classes.pill} ${classes[cfg.cls]}`}
    />
  );
};

// "há 5 dias" / "hoje" / "em 3 dias"
export const relativeDue = (dueDate) => {
  const days = moment(dueDate).diff(moment().startOf("day"), "days");
  if (days === 0) return "hoje";
  if (days === 1) return "amanhã";
  if (days === -1) return "ontem";
  return days < 0 ? `há ${Math.abs(days)} dias` : `em ${days} dias`;
};

// ─── Faixa de indicadores ────────────────────────────────────────────────────
// items: [{ label, value, caption, captionTone, tone, colorValue }]
export const KpiStrip = ({ items, classes, theme, loading }) => (
  <div className={classes.kpis}>
    {items.map((item) => (
      <div className={classes.kpi} key={item.label}>
        <div className={classes.kpiLabel}>
          <span className={classes.kpiDot} style={{ backgroundColor: toneColor(theme, item.tone) }} />
          {item.label}
        </div>
        <div
          className={classes.kpiValue}
          style={item.colorValue ? { color: toneColor(theme, item.tone) } : undefined}
        >
          {loading ? "—" : item.value}
        </div>
        <div
          className={classes.kpiCaption}
          style={item.captionTone ? { color: toneColor(theme, item.captionTone) } : undefined}
        >
          {loading ? " " : item.caption || " "}
        </div>
      </div>
    ))}
  </div>
);

// ─── Filtro de período em chips ──────────────────────────────────────────────
export const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "month", label: "Este mês" },
  { key: "lastMonth", label: "Mês passado" },
  { key: "quarter", label: "Últimos 3 meses" },
  { key: "year", label: "Este ano" },
  { key: "custom", label: "Personalizado" },
];

export const periodRange = (key) => {
  const f = "YYYY-MM-DD";
  switch (key) {
    case "today":
      return [moment().format(f), moment().format(f)];
    case "lastMonth":
      return [
        moment().subtract(1, "month").startOf("month").format(f),
        moment().subtract(1, "month").endOf("month").format(f),
      ];
    case "quarter":
      return [moment().subtract(2, "month").startOf("month").format(f), moment().endOf("month").format(f)];
    case "year":
      return [moment().startOf("year").format(f), moment().endOf("year").format(f)];
    case "month":
    default:
      return [moment().startOf("month").format(f), moment().endOf("month").format(f)];
  }
};

// Título legível do período para o cabeçalho ("Outubro de 2026", "01/10 a 15/10").
export const periodTitle = (key, from, to) => {
  if (key === "month" || key === "lastMonth") {
    const s = moment(from).format("MMMM [de] YYYY");
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  if (key === "today") return "Hoje";
  return `${moment(from).format("DD/MM/YYYY")} a ${moment(to).format("DD/MM/YYYY")}`;
};

// state: { key, from, to } — onChange recebe o novo state.
export const PeriodFilter = ({ value, onChange, classes, periods = PERIODS, scroll }) => (
  <>
    <div
      className={`${classes.chipGroup} ${scroll ? classes.chipGroupScroll : ""}`}
      role="group"
      aria-label="Período"
    >
      {periods.map((p) => {
        const active = value.key === p.key;
        return (
          <Chip
            key={p.key}
            clickable
            className={classes.filterChip}
            label={p.label}
            color={active ? "primary" : "default"}
            variant={active ? "default" : "outlined"}
            aria-pressed={active}
            onClick={() => {
              if (p.key === "custom") onChange({ ...value, key: "custom" });
              else {
                const [from, to] = periodRange(p.key);
                onChange({ key: p.key, from, to });
              }
            }}
          />
        );
      })}
    </div>
    {value.key === "custom" && (
      <div className={classes.chipGroup}>
        <TextField
          label="De"
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={value.from}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
          className={classes.dateField}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={value.to}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className={classes.dateField}
        />
      </div>
    )}
  </>
);

export const defaultPeriod = () => {
  const [from, to] = periodRange("month");
  return { key: "month", from, to };
};

// ─── CSV ─────────────────────────────────────────────────────────────────────
const csvCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Separador ";" e BOM para abrir direto no Excel em pt-BR.
export const downloadCsv = (filename, header, rows) => {
  const lines = [header, ...rows].map((r) => r.map(csvCell).join(";"));
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
